import { CB } from '../../combat/spriteConfig';

// ─── Subfolder Resolution ─────────────────────────────────────────────────────

/** Derive the processed icon subfolder from an icon key */
function iconSubfolder(iconKey: string): string {
  if (iconKey.startsWith('icon_ability_')) return 'abilities';
  if (iconKey.startsWith('icon_mastery_')) return 'masteries';
  if (iconKey.startsWith('icon_status_')) return 'status';
  if (iconKey.startsWith('icon_element_')) return 'elements';
  if (iconKey.startsWith('icon_unique_')) return 'uniques';
  if (iconKey.startsWith('icon_currency_')) return 'currencies';
  if (iconKey.startsWith('icon_dye_')) return 'dyes';
  return 'items';
}

/** Build a full icon URL from an icon key */
function iconUrl(iconKey: string, sm = true): string {
  const sub = iconSubfolder(iconKey);
  const suffix = sm ? '_sm' : '';
  return `/activity/sprites/processed/icons/${sub}/${iconKey}${suffix}.webp${CB}`;
}

// ─── Item Icons ───────────────────────────────────────────────────────────────

/** Maps item base IDs to icon keys for display */
const ITEM_BASE_TO_ICON: Record<string, string> = {
  // Sword family
  shortsword: 'icon_item_shortsword', cutlass: 'icon_item_cutlass',
  arming_sword: 'icon_item_arming_sword', broadsword: 'icon_item_broadsword',
  longsword: 'icon_item_longsword', falchion: 'icon_item_falchion',
  estoc: 'icon_item_estoc', flamberge: 'icon_item_flamberge',
  // Hammer family
  cudgel: 'icon_item_cudgel', club: 'icon_item_club', mace: 'icon_item_mace',
  flanged_mace: 'icon_item_flanged_mace', war_mace: 'icon_item_war_mace',
  morningstar: 'icon_item_morningstar', warhammer: 'icon_item_warhammer', maul: 'icon_item_maul',
  // Staff family
  ashwood_staff: 'icon_item_ashwood_staff', willow_staff: 'icon_item_willow_staff',
  ironwood_staff: 'icon_item_ironwood_staff', crystal_staff: 'icon_item_crystal_staff',
  runestave: 'icon_item_runestave', resonant_staff: 'icon_item_resonant_staff',
  empyrean_staff: 'icon_item_empyrean_staff',
  // Trident family (7 tiers, Tidemancy)
  reedspear: 'icon_item_reedspear', brine_trident: 'icon_item_brine_trident',
  boarding_trident: 'icon_item_boarding_trident', harbor_trident: 'icon_item_harbor_trident',
  coral_trident: 'icon_item_coral_trident', tideforged_trident: 'icon_item_tideforged_trident',
  empyrean_trident: 'icon_item_empyrean_trident',
  // Spear family (5 tiers, Skirmistry)
  brass_lance: 'icon_item_brass_lance', charged_lance: 'icon_item_charged_lance',
  silverline_lance: 'icon_item_silverline_lance', stormtuned_lance: 'icon_item_stormtuned_lance',
  empyrean_lance: 'icon_item_empyrean_lance',
  // Dagger family
  shiv: 'icon_item_shiv', knife: 'icon_item_knife', dirk: 'icon_item_dirk',
  stiletto: 'icon_item_stiletto', kris: 'icon_item_kris',
  misericorde: 'icon_item_misericorde', cinquedea: 'icon_item_cinquedea',
  // Wand family (Illusionism re-gate, parked)
  hazel_wand: 'icon_item_hazel_wand', yew_wand: 'icon_item_yew_wand',
  crystal_wand: 'icon_item_crystal_wand', ivory_wand: 'icon_item_ivory_wand',
  runic_wand: 'icon_item_runic_wand', leyline_wand: 'icon_item_leyline_wand',
  // Fist family
  wraps: 'icon_item_wraps', knuckles: 'icon_item_knuckles', gauntlet: 'icon_item_gauntlet',
  cestus: 'icon_item_cestus', tekko: 'icon_item_tekko', pankration: 'icon_item_pankration',
  // Bow family
  sapling_bow: 'icon_item_sapling_bow', crude_bow: 'icon_item_crude_bow',
  shortbow: 'icon_item_shortbow', hunting_bow: 'icon_item_hunting_bow',
  longbow: 'icon_item_longbow', recurve: 'icon_item_recurve',
  composite_bow: 'icon_item_composite_bow', greatbow: 'icon_item_greatbow',
  // Fan family
  paper_fan: 'icon_item_paper_fan', silk_fan: 'icon_item_silk_fan',
  iron_fan: 'icon_item_iron_fan', war_fan: 'icon_item_war_fan', tessen: 'icon_item_tessen',
  // Scythe family (5 tiers, Necrotics) — dark caster, death-mage
  hand_scythe: 'icon_item_hand_scythe', harvest_scythe: 'icon_item_harvest_scythe',
  bone_scythe: 'icon_item_bone_scythe', grave_scythe: 'icon_item_grave_scythe',
  empyrean_scythe: 'icon_item_empyrean_scythe',
  // Sceptre family (8 tiers, Theurgy) — healing 1h caster
  rod: 'icon_item_rod', crook: 'icon_item_crook', cane: 'icon_item_cane',
  crozier: 'icon_item_crozier', sceptre: 'icon_item_sceptre', verge: 'icon_item_verge',
  aureole: 'icon_item_aureole', empyrean_sceptre: 'icon_item_empyrean_sceptre',
  // Axe family (8 tiers, Axecraft)
  hatchet: 'icon_item_hatchet', hand_axe: 'icon_item_hand_axe',
  war_axe: 'icon_item_war_axe', bardiche: 'icon_item_bardiche',
  bearded_axe: 'icon_item_bearded_axe', battleaxe: 'icon_item_battleaxe',
  pollaxe: 'icon_item_pollaxe', dane_axe: 'icon_item_dane_axe',
  // Gun family (7 tiers, Artifice)
  handgonne: 'icon_item_handgonne', coilshot: 'icon_item_coilshot',
  hammerlock: 'icon_item_hammerlock', hexbore: 'icon_item_hexbore',
  fusewright: 'icon_item_fusewright', cogheart: 'icon_item_cogheart',
  whistlewright: 'icon_item_whistlewright',
  // Leather family (7 tiers)
  hide_jerkin: 'icon_item_hide_jerkin', boiled_leather: 'icon_item_boiled_leather',
  studded_leather: 'icon_item_studded_leather', hardened_leather: 'icon_item_hardened_leather',
  wyrmhide_jerkin: 'icon_item_wyrmhide_jerkin', drakeleather_cuirass: 'icon_item_drakeleather_cuirass',
  shadowsilk_cuirass: 'icon_item_shadowsilk_cuirass',
  // Robe family (7 tiers)
  apprentice_robe: 'icon_item_apprentice_robe', linen_robe: 'icon_item_linen_robe',
  silken_robe: 'icon_item_silken_robe', scholars_robe: 'icon_item_scholars_robe',
  mystic_robe: 'icon_item_mystic_robe', runewoven_robe: 'icon_item_runewoven_robe',
  archmages_robe: 'icon_item_archmages_robe',
  // Plate family (7 tiers, Fortification) — max Guard ceiling
  breastplate: 'icon_item_breastplate', cuirass: 'icon_item_cuirass',
  half_plate: 'icon_item_half_plate', field_plate: 'icon_item_field_plate',
  full_plate: 'icon_item_full_plate', articulated_plate: 'icon_item_articulated_plate',
  war_plate: 'icon_item_war_plate',
  // Padded family (7 tiers, Grit) — anti-spike HP soak
  padded_vest: 'icon_item_padded_vest', gambeson: 'icon_item_gambeson',
  brigandine: 'icon_item_brigandine', plated_coat: 'icon_item_plated_coat',
  studded_brigandine: 'icon_item_studded_brigandine', veterans_coat: 'icon_item_veterans_coat',
  ironclad_jack: 'icon_item_ironclad_jack',
  // Stoneplate family (7 tiers, Lithurgy) — Break-synergy stone-forged heavy
  slab_harness: 'icon_item_slab_harness', slate_plate: 'icon_item_slate_plate',
  basalt_cuirass: 'icon_item_basalt_cuirass', granite_plate: 'icon_item_granite_plate',
  obsidian_plate: 'icon_item_obsidian_plate', bedrock_carapace: 'icon_item_bedrock_carapace',
  tectonic_plate: 'icon_item_tectonic_plate',
  // Tide-woven family (7 tiers, Hydromancy) — barrier-conditional wallwalker light caster
  tide_wrap: 'icon_item_tide_wrap', brine_silk: 'icon_item_brine_silk',
  wave_silk: 'icon_item_wave_silk', tideweave: 'icon_item_tideweave',
  current_silk: 'icon_item_current_silk', stormsilk: 'icon_item_stormsilk',
  oceanweave: 'icon_item_oceanweave',
  // Bloomwoven family (7 tiers, Verdancy) — healer struck-defense engine, living plant cloth
  bracken_wrap: 'icon_item_bracken_wrap', vinecloak: 'icon_item_vinecloak',
  bloomsilk: 'icon_item_bloomsilk', bloomweave: 'icon_item_bloomweave',
  briarsilk: 'icon_item_briarsilk', sylvan_mantle: 'icon_item_sylvan_mantle',
  heartbloom_mantle: 'icon_item_heartbloom_mantle',
  // Shadeweave family (7 tiers, Obscuration) — assassin / debuff-stacker / setup-explode
  spun_shadeweave: 'icon_item_spun_shadeweave', plain_shadeweave: 'icon_item_plain_shadeweave',
  hunters_shadeweave: 'icon_item_hunters_shadeweave', prowlers_shadeweave: 'icon_item_prowlers_shadeweave',
  mistbound_shadeweave: 'icon_item_mistbound_shadeweave', whisperspun_shadeweave: 'icon_item_whisperspun_shadeweave',
  eclipsed_shadeweave: 'icon_item_eclipsed_shadeweave',
  // Lamellar family (7 tiers, Tactics) — battlefield-officer medium / Guard-hybrid
  laced_lamellar: 'icon_item_laced_lamellar', banded_lamellar: 'icon_item_banded_lamellar',
  layered_lamellar: 'icon_item_layered_lamellar', riveted_lamellar: 'icon_item_riveted_lamellar',
  lamellar_harness: 'icon_item_lamellar_harness', captains_harness: 'icon_item_captains_harness',
  marshals_harness: 'icon_item_marshals_harness',
  // Accessories — Rings
  signet_ring: 'icon_item_signet_ring', silver_signet: 'icon_item_silver_signet',
  starsilver_signet: 'icon_item_starsilver_signet',
  // Accessories — Belts
  rope_belt: 'icon_item_rope_belt', studded_belt: 'icon_item_studded_belt', warwoven_belt: 'icon_item_warwoven_belt',
  // Accessories — Shields
  buckler: 'icon_buckler', kite_shield: 'icon_kite_shield', tower_shield: 'icon_tower_shield',
  // Offhand — Idols (summoner)
  wooden_idol: 'icon_wooden_idol', bone_idol: 'icon_bone_idol', heartwood_idol: 'icon_heartwood_idol',
  // Offhand — Tomes (caster, focus pool)
  apprentice_tome: 'icon_item_apprentice_tome', scholar_tome: 'icon_item_scholar_tome',
  arcanum_tome: 'icon_item_arcanum_tome',
  // Offhand — Quivers (ranged, crit)
  leather_quiver: 'icon_item_leather_quiver', reinforced_quiver: 'icon_item_reinforced_quiver',
  warquiver: 'icon_item_warquiver',
  // Offhand — Alchemy (poison + heal hybrid)
  reagent_pouch: 'icon_item_reagent_pouch', phial_bandolier: 'icon_item_phial_bandolier',
  apothecarium: 'icon_item_apothecarium',
  // Offhand — Sigils (Zealotry Fervor anchor)
  carved_sigil: 'icon_item_carved_sigil', consecrated_sigil: 'icon_item_consecrated_sigil',
  radiant_sigil: 'icon_item_radiant_sigil',
  // Unique items
  vothalas_carapace: 'icon_unique_vothalas_carapace',
  zythaxs_hunger: 'icon_unique_zythaxs_hunger',
  hewn_rebuke: 'icon_unique_hewn_rebuke',
  first_ash: 'icon_unique_first_ash',
  quiet_fang: 'icon_unique_quiet_fang',
  oriens_mandate: 'icon_unique_oriens_mandate',
  velaras_recantation: 'icon_unique_velaras_recantation',
  menders_vow: 'icon_unique_menders_vow',
  carryover: 'icon_unique_carryover',
  censers_belt: 'icon_unique_censers_belt',
  quietspine: 'icon_unique_quietspine',
  crestwind: 'icon_unique_crestwind',
};

/** Fallback icons by category when base type isn't mapped.
 * Armor has no generic fallback — all shipped armor bases should be mapped above. */
const CATEGORY_FALLBACK: Record<string, string> = {
  weapon: 'icon_sword',
  accessory: 'icon_ring',
};

/** Maps element IDs to icon keys */
const ELEMENT_TO_ICON: Record<string, string> = {
  fire: 'icon_element_fire',
  water: 'icon_element_water',
  lightning: 'icon_element_lightning',
  nature: 'icon_element_nature',
  wind: 'icon_element_wind',
  earth: 'icon_element_earth',
  holy: 'icon_element_holy',
  dark: 'icon_element_dark',
  astral: 'icon_element_astral',
  steel: 'icon_element_steel',
  brute: 'icon_element_brute',
  finesse: 'icon_element_finesse',
};

/** Maps status effect IDs to icon keys */
const STATUS_ICON_MAP: Record<string, string> = {
  // DoTs
  burn: 'icon_status_burn',
  pyrebloom: 'icon_status_burn',
  shadow_burn: 'icon_status_shadow_burn',
  poison: 'icon_status_poison',
  bleed: 'icon_status_bleed',
  // Shields / defensive buffs
  martial_res_up: 'icon_status_hardened',
  arm_up: 'icon_status_shield',
  raised_shield: 'icon_status_raised_shield',
  intercepting: 'icon_status_intercepting',
  brace: 'icon_status_brace',
  // Fortify: Fortification mastery's stackable DR buff (gear-driven via suffix_fortify_strike).
  // Distinct from martial_res_up (renamed to "Hardened" — the Defend-action res buff).
  fortified: 'icon_status_fortify',
  // Barrier-granting buffs reuse their ability icons (no dedicated status art yet)
  brinewall: 'icon_ability_brinewall',
  tideguard: 'icon_ability_tideguard',
  surge: 'icon_ability_surge',
  iron_wall: 'icon_ability_iron_wall',
  shadow_shield: 'icon_ability_iron_wall',
  // Offensive buffs
  str_up: 'icon_status_strength',
  fire_infusion: 'icon_status_fire_infusion',
  venomcoat: 'icon_status_venomcoat',
  rallied: 'icon_status_rallied',
  standing_order: 'icon_status_standing_order',
  fury: 'icon_status_fury',
  recomposed: 'icon_ability_recompose',
  defy: 'icon_status_defy',
  intensify: 'icon_status_intensify',
  catalyze: 'icon_status_catalyze',
  acuity: 'icon_status_acuity',
  soulstitch: 'icon_status_soulstitch',
  split_aim: 'icon_status_split_aim',
  ward: 'icon_status_ward',
  // Utility buffs
  spd_up: 'icon_status_haste',
  regen: 'icon_status_regen',
  nature_regen: 'icon_status_regen',
  tonic: 'icon_status_regen',
  vanish: 'icon_status_vanish',
  preparation: 'icon_status_preparation',
  poised: 'icon_status_poised',
  // Resource stacks (target-localized)
  tempo: 'icon_status_tempo',
  // Debuffs
  stagger: 'icon_status_stagger',
  stilled: 'icon_status_stagger',
  stunned: 'icon_status_stunned',
  sunder: 'icon_status_sunder',
  brittle: 'icon_status_corroded',
  despair: 'icon_status_despair',
  spd_down: 'icon_status_slow',
  soaked: 'icon_status_soaked',
  taunt: 'icon_status_taunt',
  hunters_mark: 'icon_status_mark',
  dueled: 'icon_status_dueled',
  fractured: 'icon_status_fractured',
  exposed: 'icon_status_exposed',
  weakened: 'icon_status_weakened',
  blinded: 'icon_status_blinded',
  confusion: 'icon_status_confusion',
  rooted: 'icon_status_rooted',
  silenced: 'icon_status_silenced',
  smothered: 'icon_status_smothered',
  spore_cloud: 'icon_status_spore_cloud',
  paralyzed: 'icon_status_paralyzed',
  decay: 'icon_status_decay',
  rot: 'icon_status_rot',
  broken: 'icon_status_broken',
  drowning: 'icon_status_drowning',
  aftershock: 'icon_status_aftershock',
  trap_powderkeg: 'icon_ability_powderkeg_trap',
  trap_vise: 'icon_ability_vise_trap',
  trap_lightning_rod: 'icon_ability_lightning_rod',
  manifest_blade: 'icon_status_manifest_blade',
  sigil_of_echoes: 'icon_status_sigil_of_echoes',
  // Enemy buffs
  overgrowth: 'icon_status_raised_shield',
  lantern_vigil: 'icon_status_lantern_vigil',
  sandstorm_fury: 'icon_status_fury',
};

// ─── URL Helpers ──────────────────────────────────────────────────────────────

/** Get the icon URL for an item base type. Pass fullSize=true for the large (non-_sm) variant. */
export function getItemIconUrl(baseType: string, category?: string, fullSize = false): string {
  const iconKey = ITEM_BASE_TO_ICON[baseType] || CATEGORY_FALLBACK[category ?? 'weapon'] || 'icon_sword';
  return iconUrl(iconKey, !fullSize);
}

/** Get the icon URL for an element */
export function getElementIconUrl(element: string): string {
  const iconKey = ELEMENT_TO_ICON[element] || 'icon_element_steel';
  return iconUrl(iconKey);
}

/** Get the icon URL for a status effect */
export function getStatusIconUrl(effectId: string): string {
  const iconKey = STATUS_ICON_MAP[effectId] || 'icon_status_burn';
  return iconUrl(iconKey);
}

/** Get the icon URL for a mastery */
export function getMasteryIconUrl(masteryId: string): string {
  return iconUrl(`icon_mastery_${masteryId}`);
}

/** Get the icon URL for an ability */
export function getAbilityIconUrl(abilityId: string): string {
  return iconUrl(`icon_ability_${abilityId}`);
}

// ─── Render Helpers ───────────────────────────────────────────────────────────

/** Render an item icon <img> tag */
export function renderItemIcon(baseType: string, category?: string, size: 'sm' | 'md' | 'lg' = 'md'): string {
  const src = getItemIconUrl(baseType, category);
  const px = size === 'sm' ? 28 : size === 'md' ? 40 : 56;
  return `<img class="item-icon item-icon--${size}" src="${src}" alt="" width="${px}" height="${px}" draggable="false">`;
}

/** Render an element icon <img> tag */
export function renderElementIcon(element: string, size: 'sm' | 'md' | 'lg' = 'md'): string {
  const src = getElementIconUrl(element);
  const px = size === 'sm' ? 28 : size === 'md' ? 40 : 56;
  return `<img class="item-icon element-icon element-icon--${size}" src="${src}" alt="${element}" width="${px}" height="${px}" draggable="false">`;
}

/** Render a mastery icon <img> tag */
export function renderMasteryIcon(masteryId: string, size: 'sm' | 'md' | 'lg' = 'sm'): string {
  const src = getMasteryIconUrl(masteryId);
  const px = size === 'sm' ? 18 : size === 'md' ? 28 : 40;
  return `<img class="mastery-icon-img mastery-icon-img--${size}" src="${src}" alt="" width="${px}" height="${px}" draggable="false">`;
}

/** Render a status effect icon <img> tag */
export function renderStatusIcon(effectId: string, size: 'sm' | 'md' | 'lg' = 'sm'): string {
  const src = getStatusIconUrl(effectId);
  const px = size === 'sm' ? 28 : size === 'md' ? 40 : 56;
  return `<img class="status-icon status-icon--${size}" src="${src}" alt="" width="${px}" height="${px}" draggable="false">`;
}

/** Render an ability icon <img> tag */
export function renderAbilityIcon(abilityId: string, size: 'sm' | 'md' | 'lg' = 'sm'): string {
  const src = getAbilityIconUrl(abilityId);
  const px = size === 'sm' ? 28 : size === 'md' ? 40 : 56;
  return `<img class="ability-icon ability-icon--${size}" src="${src}" alt="" width="${px}" height="${px}" draggable="false">`;
}

// ─── Currency Icons ──────────────────────────────────────────────────────────

/** camelCase currency ID → snake_case filename segment */
const CURRENCY_ID_TO_FILE: Record<string, string> = {
  gold: 'gold',
  soulthread: 'soulthread',
  vividSpools: 'vivid_spools',
  prismaticSpools: 'prismatic_spools',
  bindings: 'bindings',
  shears: 'shears',
  lustrousYarn: 'lustrous_yarn',
  gildedYarn: 'gilded_yarn',
  fibers: 'fibers',
  faintMemory: 'faint_memory',
  stirringMemory: 'stirring_memory',
  brightMemory: 'bright_memory',
  radiantMemory: 'radiant_memory',
};

/** Get the icon URL for a currency */
export function getCurrencyIconUrl(currencyId: string): string {
  const file = CURRENCY_ID_TO_FILE[currencyId] ?? currencyId;
  return iconUrl(`icon_currency_${file}`, true);
}

/** Render a currency icon <img> tag. Default 16px for header use. */
export function renderCurrencyIcon(currencyId: string, size = 16): string {
  const src = getCurrencyIconUrl(currencyId);
  return `<img class="currency-icon" src="${src}" alt="" width="${size}" height="${size}" draggable="false">`;
}

// ─── Dye Icons ────────────────────────────────────────────────────────────────

/** Get the icon URL for a dye color (single icon per color, tier conveyed
 *  by chip text in the picker — the bottle art is the same across tiers). */
export function getDyeIconUrl(color: string): string {
  return iconUrl(`icon_dye_${color}`, true);
}

/** Render a dye icon <img> tag. Default size matches the picker row. */
export function renderDyeIcon(color: string, size = 32): string {
  const src = getDyeIconUrl(color);
  return `<img class="dye-icon" src="${src}" alt="" width="${size}" height="${size}" draggable="false">`;
}
